import { cn } from '@/shadcn-ui/utils';
import { Label } from '@/shadcn-ui/label';
import { Input } from '@/shadcn-ui/input';
import Spacer from '@/common/components/spacer';
import React, { useState } from 'react';
import { PaymentType } from '@/account/types/payment-method-configuration';

interface StripeCardholderNameProps {
  stripePaymentElementRendered: boolean;
  usingStoredPayment: boolean;
  paymentError?: string;
  paymentType?: PaymentType;
}

/**
 * Renders a cardholder name input field (with label and validation hint) that matches styling of the Stripe PaymentElement.
 * Also appends a hidden using_stored_payment form field (if applicable) to use for validation
 */
export default function StripeCardholderName({
  stripePaymentElementRendered,
  usingStoredPayment,
  paymentError,
  paymentType = PaymentType.card,
}: StripeCardholderNameProps) {
  const [cardholderName, setCardholderName] = useState('');
  const cardholderNameMissing =
    paymentError === 'Please enter cardholder name.' && cardholderName === '';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCardholderName(event.target.value);
  };

  return (
    <>
      {stripePaymentElementRendered && !usingStoredPayment ? (
        <div className="flex flex-col gap-0.5">
          <Label
            id="name-label"
            className="font-[-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif] text-[0.93rem] text-[#6d6e78]"
          >
            {paymentType === PaymentType.card ? 'Cardholder' : 'Account holder'} name
            <span className="ml-[1px]">*</span>
          </Label>
          <Input
            aria-labelledby="name-label"
            id="name"
            name="name"
            className={cn(
              "font-[-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif] rounded-[5px] border-[1px] border-[#e6e6e6] text-[#6d6e78] placeholder-[#757680] shadow-[0px_1px_1px_rgba(0,0,0,0.03),0px_3px_6px_rgba(0,0,0,0.02)]",
              {
                'border-[2px] border-[#df1b41]': cardholderNameMissing,
              }
            )}
            onChange={handleChange}
          />
          {cardholderNameMissing ? (
            <p className="font-[-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif] text-[0.93rem] font-light text-[#df1b41]">
              Your {paymentType === PaymentType.card ? 'cardholder' : 'account holder'}{' '}
              name is incomplete.
            </p>
          ) : null}
          <Spacer variant="height4" />
        </div>
      ) : null}

      {usingStoredPayment ? (
        <input type="hidden" name="using_stored_payment" value="true" />
      ) : null}
    </>
  );
}
